import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import swal from '@sweetalert/with-react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';

import { toast } from 'react-toastify';
import { Creators as CourseActions } from '../../store/ducks/newCourse/actions';

import SurveyView from './subcomponents/SurveyView';
import ActionFooter from './subcomponents/ActionFooter';
import AssessmentModal from './subcomponents/Assessment';
import Header from './subcomponents/Header';
import { resolveDraftResume } from './draftResume';
import { loadEntranceExamDraft } from './entranceExamDraft';
import { showTimeOverAlert, showNotReleasedAlert } from './entranceExamAlerts';
import { submitAfterTimeOver } from './entranceExamTimeOver';
import {
  STATUS_ACTION,
  STATUS_NOTICE_MESSAGES,
  resolvePollingAction
} from './vestibularStatus';
import ExitAlert from './subcomponents/ExitAlert';
import FinishAlert from './subcomponents/FinishAlert';
import spinner from '../../images/paul_loading.gif';
import { gateway } from '../../utils/api';
import history from '../../utils/history';

import { Container, Loading, Content } from './styles';
import NavBarSuperior from '../NavBarSuperior/NavBarSuperior';
import { checkIsVertibular, toastDefaultOptions } from '../../utils/functions';
import { notifyAssessmentFinished } from '../../utils/assessmentFinishedEvent';

function Assessment({ location }) {
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFinishAssessment, setLoadingFinishAssessment] = useState(false);
  const [currentSurveyIndex, setCurrentSurveyIndex] = useState(0);
  const [answeredSurveys, setAnsweredSurveys] = useState([]);
  const [unAnsweredSurveys, setUnAnsweredSurveys] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [actionsDisabled, setActionsDisabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [userComment, setUserComment] = useState('');
  const [isBlockingActions, setIsBlockingActions] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [vestibularChecked, setVestibularChecked] = useState(false);
  const [timeOverPending, setTimeOverPending] = useState(false);

  const skipCloseMessage = useRef(false);
  const ownSubmission = useRef(false);
  const dispatch = useDispatch();

  const closeWindow = () => {
    skipCloseMessage.current = true;
    window.close();
  };

  const currentSurvey = useMemo(() => {
    return {
      data: _.get(assessmentData, `surveys.${currentSurveyIndex}`, null),
      answers: _.get(answers, `${currentSurveyIndex}.alternatives`, null)
    };
  }, [assessmentData, currentSurveyIndex, answers]);

  const numberOfSurveys = useMemo(() => {
    return assessmentData ? _.size(assessmentData.surveys || []) : 0;
  }, [assessmentData]);

  function handleErrorAlert() {
    swal({
      title: `Ops, ocorreu um erro.`,
      text: `Não foi possível buscar os dados dessa avaliação.`,
      buttons: {
        confirm: {
          text: 'Continuar',
          value: true,
          visible: true,
          className: 'btn-alert btn-alert-confirm-onboarding'
        }
      }
    }).then(() => {
      skipCloseMessage.current = true;
      window.close();
    });
  }

  useEffect(() => {
    const isVestibular = checkIsVertibular();
    if (!isVestibular) {
      window.addEventListener('beforeunload', e => {
        if (!skipCloseMessage.current) {
          const confirmationMessage =
            'Suas alterações não serão salvas e uma tentativa será contabilizada!';
          (e || window.event).returnValue = confirmationMessage;
          return confirmationMessage;
        }

        return null;
      });
    }
  }, []);

  function findIndexOfLastAnsweredAnswer(answers) {
    const indexOfLastAnsweredAnswer = answers
      .map((question, index) =>
        question.alternatives.some(alternative => alternative.value === true)
          ? index
          : -1
      )
      .filter(index => index !== -1)
      .pop();

    return {
      indexOfLastAnsweredAnswer
    };
  }
  async function fetchAssessmentData() {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const assessmentType = params.get('type');
    const courseCode = params.get('course');
    try {
      console.log('Buscando dados da API');
      let url = `/webbff/lit_app_web/evaluation/${code}`;
      url +=
        assessmentType === 'SELF_ASSESSMENT' && courseCode
          ? `/course/${courseCode}`
          : '';
      const response = await gateway.get(url);
      const initialAnswers = (response.surveys || []).map(survey => {
        return {
          surveyCode: survey.code,
          sessionId: survey.sessionId,
          alternatives: (survey.alternatives || []).map(alternative => {
            return {
              alternativeId: alternative.id,
              value: ''
            };
          })
        };
      });
      setAnswers(initialAnswers);
      setAssessmentData(response);
      const requestBody = {
        initialAnswers,
        response,
        savedAt: new Date().getTime()
      };
      await gateway.post(
        `/response/tempResponseUser/saveTempResponse`,
        requestBody
      );
      setLoading(false);
    } catch (error) {
      handleErrorAlert();
      setLoading(false);
    }
  }

  async function updateInitialAnswers(answers, assessmentData) {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const idProfile = localStorage.getItem('idProfile');
    const tempResponseCode = `RESPONSE_${idProfile}_${code}`;
    const requestBody = {
      code: tempResponseCode,
      response: assessmentData,
      initialAnswers: answers
    };
    await gateway
      .put(`/response/tempResponseUser/updateTempResponse`, requestBody)
      .catch(error => {
        console.log('UPDATE ERROR', error);
      });
  }

  async function desactiveTempResponse() {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const idProfile = localStorage.getItem('idProfile');
    const tempResponseCode = `RESPONSE_${idProfile}_${code}`;
    await gateway.put(
      `/response/tempResponseUser/desactiveTempResponse?code=${tempResponseCode}`
    );
  }

  function convertAlternativeValuesToBoolean(answers) {
    return answers.map(answer => ({
      ...answer,
      alternatives: answer.alternatives.map(alternative => ({
        ...alternative,
        value:
          _.first(alternative.value) === true ||
          _.first(alternative.value) === 'true'
      }))
    }));
  }

  function fillIndexesOfAnsweredQuestions(answers) {
    const questionsAnsweredIndices = answers
      .map((question, index) =>
        question.alternatives.some(alternative => alternative.value === true)
          ? index
          : -1
      )
      .filter(index => index !== -1);

    const lastIndex = Math.max(...questionsAnsweredIndices);

    const indexesAllAnsweredQuestions = Array.from(
      { length: lastIndex },
      (_, i) => i
    );

    const skippedQuestions = indexesAllAnsweredQuestions.filter(
      index => !questionsAnsweredIndices.includes(index)
    );

    return { questionsAnsweredIndices, skippedQuestions };
  }

  function applyDraft({ initialAnswers, updatedResponse }) {
    const answers = convertAlternativeValuesToBoolean(initialAnswers);
    const { indexOfLastAnsweredAnswer } = findIndexOfLastAnsweredAnswer(
      answers
    );
    const {
      questionsAnsweredIndices,
      skippedQuestions
    } = fillIndexesOfAnsweredQuestions(answers);
    setAnswers(answers);
    setCurrentSurveyIndex(indexOfLastAnsweredAnswer || 0);
    setAnsweredSurveys(questionsAnsweredIndices || 0);
    setUnAnsweredSurveys(skippedQuestions);
    setAssessmentData(updatedResponse);
    setLoading(false);
  }

  function prepareTimeOverSubmission(draft) {
    setAnswers(convertAlternativeValuesToBoolean(draft.initialAnswers));
    setAssessmentData(draft.response);
    setTimeOverPending(true);
  }

  function goToWaitingRoom() {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const assessmentType = params.get('type');
    const courseCode = params.get('course');
    history.replace(
      `/assessment-waiting-room?code=${code}&type=${assessmentType}&courseCrCode=${courseCode}`
    );
  }

  async function loadVestibularAssessment(code) {
    try {
      await loadEntranceExamDraft({
        code,
        onResume: ({ draft, timeLimit }) =>
          applyDraft({
            initialAnswers: draft.initialAnswers,
            updatedResponse: { ...draft.response, timeLimit }
          }),
        onTimeOver: prepareTimeOverSubmission,
        onNotReleased: () => showNotReleasedAlert().then(goToWaitingRoom)
      });
    } catch (error) {
      handleErrorAlert();
      setLoading(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const idProfile = localStorage.getItem('idProfile');
    const tempResponseCode = `RESPONSE_${idProfile}_${code}`;
    async function loadAssessment() {
      if (checkIsVertibular()) {
        await loadVestibularAssessment(code);
        return;
      }
      const tempResponse = await gateway.get(
        `/response/tempResponseUser/getTempResponse/${tempResponseCode}`
      );
      if (
        tempResponse &&
        tempResponse !== 'null' &&
        tempResponse !== 'undefined'
      ) {
        const { initialAnswers, response } = tempResponse;
        const { canResume, timeLimit } = resolveDraftResume(
          tempResponse,
          new Date().getTime()
        );
        if (canResume) {
          applyDraft({
            initialAnswers,
            updatedResponse: { ...response, timeLimit }
          });
        } else {
          console.log('Tempo limite atingido, buscando novos dados da API');
          await desactiveTempResponse();
          fetchAssessmentData();
        }
      } else {
        fetchAssessmentData();
      }
    }
    if (code) {
      loadAssessment();
    } else {
      handleErrorAlert();
    }
  }, []);

  useEffect(() => {
    if (answers.length > 0 && assessmentData) {
      updateInitialAnswers(answers, assessmentData);
    }
  }, [answers]);

  function handleConfirmAnswer() {
    const currentSurveyAnswers = _.get(currentSurvey, 'answers');
    const foundAnswer = currentSurveyAnswers.find(item => {
      let { value } = item;
      if (typeof item.value === 'boolean') {
        value = true;
      }
      return value;
    });
    if (foundAnswer) {
      setAnsweredSurveys(prevState => {
        if (!prevState.includes(currentSurveyIndex)) {
          return [...prevState, currentSurveyIndex];
        }
        return prevState;
      });
      if (unAnsweredSurveys.length > 0) {
        const filteredUnAnsweredSurveys = unAnsweredSurveys.filter(
          el => el !== currentSurveyIndex
        );
        setUnAnsweredSurveys(filteredUnAnsweredSurveys);
      }
      setCurrentSurveyIndex(prevState => {
        return prevState < numberOfSurveys ? prevState + 1 : prevState;
      });
    } else {
      swal({
        title: `Atenção!`,
        text: `Você não selecionou nenhuma alternativa, ao confirmar você concorda que não terá nenhuma pontuação para essa questão. `,
        buttons: {
          cancel: {
            text: 'Cancelar',
            value: false,
            visible: true,
            className: 'btn-alert btn-alert-cancel-assessment'
          },
          confirm: {
            text: 'Confirmar e avançar',
            value: true,
            visible: true,
            className: 'btn-alert btn-alert-finish-assessment'
          }
        }
      }).then(value => {
        if (value) {
          setUnAnsweredSurveys(prevState => {
            if (!prevState.includes(currentSurveyIndex)) {
              return [...prevState, currentSurveyIndex];
            }
            return prevState;
          });
          setCurrentSurveyIndex(prevState => {
            return prevState < numberOfSurveys ? prevState + 1 : prevState;
          });
        }
      });
    }
  }

  function handleGoBack() {
    setCurrentSurveyIndex(prevState => {
      return prevState > 0 ? prevState - 1 : prevState;
    });
  }

  function handleGoNext() {
    setCurrentSurveyIndex(prevState => {
      return prevState < numberOfSurveys ? prevState + 1 : prevState;
    });
  }

  async function handleFinish(forceSubmit) {
    if (
      [...answeredSurveys, ...unAnsweredSurveys].length !== numberOfSurveys &&
      !forceSubmit
    ) {
      swal({
        title: 'Atenção',
        text:
          'Você precisa responder todas questões para finalizar essa tentativa!',
        buttons: {
          confirm: {
            text: 'Ok',
            value: true,
            visible: true,
            className: 'btn-alert btn-alert'
          }
        }
      });
      return;
    }
    try {
      ownSubmission.current = true;
      setLoadingFinishAssessment(true);
      const params = new URLSearchParams(location.search);
      let selectedClassCrCode = localStorage.getItem("selectedClassCrCode") || "";
      // Fallback: em deep links ou logo após uma troca de tenant (que limpa a
      // turma selecionada), a turma pode estar vazia. Recupera a turma do
      // tenant atual para que a submissão fique vinculada à turma/tenant certos
      // (evita gravar no tenant errado e email com TURMA vazia).
      if (!selectedClassCrCode) {
        try {
          const classes = await gateway('/webbff/lit_app_web/classes');
          if (Array.isArray(classes) && classes.length > 0) {
            selectedClassCrCode = classes[0].organizationCode || "";
            if (selectedClassCrCode) {
              localStorage.setItem('selectedClassName', classes[0].name);
              localStorage.setItem('selectedClassCrCode', selectedClassCrCode);
            }
          }
        } catch (error) {
          console.error('Não foi possível recuperar a turma para a submissão:', error);
        }
      }
      const courseCode = params.get('course');
      const ucCrCode = params.get('ucCrCode');
      const requestBody = {
        assessmentCode: assessmentData.assessmentCode,
        courseCode: assessmentData.courseCode,
        courseCrCode: courseCode,
        response: {
          responseCode: assessmentData.responseCode,
          surveys: answers
        },
        userComment, 
        ucCrCode: ucCrCode,
        classCrCode: selectedClassCrCode
      };

      const retorno = await gateway.post(
        `/webbff/lit_app_web/evaluation/response/send`,
        requestBody
      );
      notifyAssessmentFinished({
        assessmentCode: assessmentData.assessmentCode,
        courseCode: courseCode || assessmentData.courseCode
      });

      await desactiveTempResponse();
      
      const examResponseCrCode = retorno.ox_standard.ox_identification.cr_code;
      const assessmentSummary = await gateway.get(
        `webbff/lit_app_web/evaluation/${assessmentData.assessmentCode}/summary`
      );
      const examResponseCorrectionIsDone = assessmentSummary.find(
        assessment => assessment.crCode === examResponseCrCode
      );

      const graphics = _.get(retorno, 'progress.graphics', []);
      setAssessment(graphics);
      dispatch(CourseActions.setRefreshCourse(true));
      swal({
        content: (
          <FinishAlert
            type={assessmentData.assessmentType}
            examResponseCrCode={examResponseCrCode}
          />
        ),
        buttons: {
          confirm: {
            text: 'OK',
            value: true,
            visible: true,
            className: 'btn-alert btn-alert-finish-assessment'
          }
        },
        closeOnClickOutside: false,
        closeOnEsc: false
      }).then(value => {
        const isVestibular = checkIsVertibular();

        if (isVestibular) {
          closeWindow();
        }

        if (value) {
          if (assessmentData.assessmentType !== 'FINAL_EXAM') {
            if (graphics.length > 0) {
              setShowModal(graphics.length > 0);
              setResponseData({
                examResponseCorrectionIsDone,
                examResponseCrCode
              });
            } else {
              let lastTrackingStatus = '';
              const { tracking = null } = examResponseCorrectionIsDone;

              if (tracking && tracking[tracking.length - 1].status)
                lastTrackingStatus = tracking[tracking.length - 1].status;

              if (
                examResponseCorrectionIsDone &&
                lastTrackingStatus.toLowerCase() === 'corrigido'
              ) {
                return history.push(
                  `/assessment-view?assessmentCode=${assessmentData.assessmentCode}&responseCode=${examResponseCrCode}`
                );
              }
              setIsBlockingActions(true);
              closeWindow();
            }
          }
          if (assessmentData.assessmentType === 'FINAL_EXAM') {
            localStorage.setItem('openProgress', JSON.stringify(true));
            setIsBlockingActions(true);
            closeWindow();
          }
        }
      });
      return true;
    } catch (error) {
      ownSubmission.current = false;
      if (!checkIsVertibular()) {
        await desactiveTempResponse();
      }
      swal({
        title: `Ops, ocorreu um erro.`,
        text: `Não foi possível enviar as respostas dessa sua tentativa, tente novamente!`,
        buttons: {
          confirm: {
            text: 'Ok',
            value: true,
            visible: true,
            className: 'btn-alert btn-alert'
          }
        }
      });
      return false;
    } finally {
      setLoadingFinishAssessment(false);
    }
  }

  function finishAfterTimeOver() {
    return submitAfterTimeOver({
      showAlert: showTimeOverAlert,
      submit: () => handleFinish(true),
      onFailed: goToWaitingRoom
    });
  }

  useEffect(() => {
    if (timeOverPending) {
      finishAfterTimeOver();
    }
  }, [timeOverPending]);

  function handleExit() {
    swal({
      content: <ExitAlert />,
      buttons: {
        confirm: {
          text: 'Sair',
          value: true,
          visible: true,
          className: 'btn-alert btn-alert-exit-assessment'
        },
        cancel: {
          text: 'Permanecer',
          value: false,
          visible: true,
          className: 'btn-alert btn-alert-stay-assessment'
        }
      }
    }).then(value => {
      if (value) {
        closeWindow();
      }
    });
  }

  const handleAnswerChange = useCallback(
    (selectedAlternativeIndex, value) => {
      setAnswers(prevState => {
        const newState = prevState.map((surveyAnswers, surveyIndex) => {
          if (surveyIndex === currentSurveyIndex) {
            return {
              ...surveyAnswers,
              alternatives: (surveyAnswers.alternatives || []).map(
                (alternative, alternativeIndex) => {
                  if (alternativeIndex === selectedAlternativeIndex) {
                    return {
                      ...alternative,
                      value
                    };
                  }

                  if (
                    currentSurvey.data.type === 'SINGLE_CHOICE' ||
                    currentSurvey.data.type === 'PROGRESSIVE'
                  ) {
                    return {
                      ...alternative,
                      value: false
                    };
                  }

                  return alternative;
                }
              )
            };
          }

          return surveyAnswers;
        });
        return newState;
      });
    },
    [currentSurveyIndex, currentSurvey]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    skipCloseMessage.current = true;
    closeWindow();
  }, []);

  useEffect(() => {
    let intervalId = null;
    let toastShown = false; // impede múltiplos toasts

    function checkVestibularWorkFlow() {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const assessmentType = params.get('type');
        const courseCode = params.get('course');

        const isVestibular = checkIsVertibular();
        if (!isVestibular) return;

        intervalId = setInterval(async () => {
          try {
            const idProfile = localStorage.getItem('idProfile');
            if (!idProfile || !code) return;

            const { status } = await gateway.get(
              `/response/response/status/${idProfile}/${code}`
            );

            setVestibularChecked(true);

            const statusAction = resolvePollingAction(
              status,
              ownSubmission.current
            );

            if (statusAction !== STATUS_ACTION.NONE && !toastShown) {
              toastShown = true; // impede novo toast
              toast(STATUS_NOTICE_MESSAGES[statusAction], {
                ...toastDefaultOptions,
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 8500
              });

              setTimeout(() => {
                history.replace(
                  `/assessment-waiting-room?code=${code}&type=${assessmentType}&courseCrCode=${courseCode}`
                );
              }, 3000);

              if (intervalId) clearInterval(intervalId);
            }
          } catch (error) {
            console.error('Erro ao requisitar endpoint:', error);
          }
        }, 5000);
      } catch (error) {
        console.error('checkVestibularWorkFlow', error);
      }
    }

    checkVestibularWorkFlow();

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [assessmentData]);

  return (
    <>
      {loading && (
        <Loading>
          <img
            alt="loading"
            style={{
              maxHeight: '85%',
              animation: 'loading-spin infinite 10s linear'
            }}
            src={spinner}
          />
        </Loading>
      )}
      {!loading && assessmentData && (
        <>
          <NavBarSuperior courses onlyLogo />
          <Container>
            <Header
              allowChange={assessmentData.allowReturn}
              numberOfQuestions={numberOfSurveys}
              limitTime={assessmentData.timeLimit}
              currentValue={currentSurveyIndex}
              concludes={answeredSurveys}
              notCompleted={unAnsweredSurveys}
              onChange={setCurrentSurveyIndex}
              onFinish={() =>
                swal({
                  title: `Ops, acabou o tempo.`,
                  text: `Infelizmente você não terminou a avaliação antes do tempo, clique abaixo para fechar a avaliação.`,
                  buttons: {
                    confirm: {
                      text: 'Continuar',
                      value: true,
                      visible: true,
                      className: 'btn-alert btn-alert-confirm-onboarding'
                    }
                  }
                }).then(value => {
                  if (value) {
                    handleFinish(true);
                  }
                })
              }
            />
            <Content>
              <main>
                {isBlockingActions ? (
                  <p>
                    Você já finalizou esta avaliação! Por favor, volte para a
                    página do curso para continuar seus estudos.
                  </p>
                ) : (
                  <>
                    {currentSurveyIndex === numberOfSurveys &&
                      assessmentData.assessmentType !== 'FINAL_EXAM' && (
                        <p>
                          Você chegou ao fim, clique em Enviar para finalizar.
                          {/* Você chegou ao fim, clique em Finalizar para ver nossa
                          recomendação de quais conteúdos você poderia pular,
                          pois já tem conhecimento sobre o tema, e quais deve
                          focar. */}
                        </p>
                      )}

                    {currentSurveyIndex === numberOfSurveys &&
                      assessmentData.assessmentType === 'FINAL_EXAM' && (
                        <p>
                          Você chegou ao fim, clique em Enviar para finalizar.
                          {/* Você chegou ao fim, clique em Finalizar para enviar
                          suas respostas e conferir quais módulos você precisa
                          estudar mais. */}
                        </p>
                      )}

                    {currentSurveyIndex !== numberOfSurveys && (
                      <SurveyView
                        surveyData={currentSurvey.data}
                        surveyAnswers={currentSurvey.answers}
                        onAnswerChange={handleAnswerChange}
                        onUserComment={setUserComment}
                        userComment={userComment}
                        numberQuestion={currentSurveyIndex + 1}
                        onDisableActions={disabled => {
                          setActionsDisabled(disabled);
                        }}
                        assessmentCode={assessmentData && assessmentData.assessmentCode}
                        studentCode={assessmentData && assessmentData.responseCode}
                      />
                    )}
                  </>
                )}
              </main>

              {showModal && (
                <AssessmentModal
                  showModal={showModal}
                  closeModal={handleCloseModal}
                  assessment={assessment}
                />
              )}
            </Content>
            <footer>
              <ActionFooter
                disabled={actionsDisabled || isBlockingActions}
                allowGoBack={assessmentData.allowReturn}
                onGoBack={handleGoBack}
                onGoNext={handleGoNext}
                onConfirm={handleConfirmAnswer}
                showFinishButton={currentSurveyIndex === numberOfSurveys}
                disableNextButton={currentSurveyIndex === numberOfSurveys - 1}
                onFinish={handleFinish}
                onExit={handleExit}
                loading={loadingFinishAssessment}
              />
            </footer>
          </Container>
        </>
      )}
    </>
  );
}

export default Assessment;
